import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Quote,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import BookingSection from "@/components/BookingSection";

const HERO_IMG =
  "https://images.squarespace-cdn.com/content/v1/68ed093ff347041b7a509c3f/1760364864132-VS0A9Q4TN8PJKRDBIW00/Aminah+A%CC%8Adin+teoch+bokhylla.jpeg";
const ABOUT_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_transform-together-1/artifacts/8xtni174_Aminah%20A%CC%8Adin%20Original.webp";

const painPoints = [
  {
    title: "Kampen mot känslorna",
    text: "Du bär på tankar och känslor som oro, stress och sorg. Det påverkar hur du mår och gör att vardagen kan kännas ansträngd. Du vill bli vän med dina tankar och känslor och känna dig lugn inombords – utan att behöva förändra dig själv eller göra stora förändringar i livet.",
  },
  {
    title: "Du har testat olika metoder",
    text: "Du har testat olika metoder för att må bättre, men det känns svårt att känna dig avslappnad och trygg i dig själv. Du längtar efter att känna dig starkare och gladare. Du vill ha ett bra stöd för att hitta metoder och vägar som passar just dig.",
  },
  {
    title: "Du vill tycka om dig själv mer",
    text: "Du klankar ner på dig själv och känner dig sällan riktigt nöjd. Du längtar efter att tycka om dig själv som du är. Du vill kliva in i din kraft och använda din potential. Du är redo att göra små förändringar som stärker dig.",
  },
];

const testimonials = [
  {
    quote:
      "Aminah har alltid pekat på den kraft som jag själv besitter att känna in i vad som är bäst att göra. Tack vare den vänlighet och respekt jag alltid blivit bemött med från Aminah har jag lärt mig att bemöta mig själv med större respekt och värme.",
    name: "Alva",
  },
  {
    quote:
      "På ett mjukt och kärleksfullt, men ändå kraftfullt sätt stöttade Aminah mig i att se min potential och kraft. Jag kan inte varmt nog rekommendera henne som mentor och föreläsare.",
    name: "Anna",
  },
  {
    quote:
      "Aminah blev min mentor när jag letade efter ett mer hållbart sätt att leva. Med Aminah som stöd kunde jag börja upptäcka att jag inte behövde gå massa omvägar för att börja slappna av och ta hand om mig själv.",
    name: "Sofie",
  },
];

const faqs = [
  {
    q: "Kostar samtalet någonting?",
    a: "Nej, samtalet är 100 % kostnadsfritt och binder dig inte till någonting.",
  },
  {
    q: "Hur går samtalet till – behöver jag ladda ner något?",
    a: "Vi ses enkelt online via en länk du får till din e-post när du bokar. Du behöver inte ladda ner några krångliga program.",
  },
  {
    q: "Behöver jag förbereda något innan vi ses?",
    a: "Nej, du behöver inte förbereda någonting alls. Vi utgår helt från var du befinner dig just nu.",
  },
  {
    q: "Vad händer efter samtalet?",
    a: "Om du känner att mitt mentorprogram eller enstaka samtal känns rätt för dig kikar vi på hur ett sådant upplägg ser ut. Om inte, har du ändå fått med dig värdefulla insikter och verktyg helt utan krav!",
  },
];

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);

  const scrollToBooking = () => {
    const el = document.getElementById("boka");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    document.title =
      "Aminah Ådin · Kostnadsfritt samtal – Från stress till inre lugn";
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2C2825]" data-testid="landing-page">
      {/* Nav */}
      <header
        data-testid="site-nav"
        style={{ top: "var(--cookie-banner-h, 0px)" }}
        className={`fixed inset-x-0 z-40 transition-all ${
          scrolled
            ? "bg-[#FAF9F6]/85 backdrop-blur-md border-b border-[#E6DFD7]"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo"
            className="font-serif-display text-xl sm:text-2xl tracking-tight text-[#2C2825]"
          >
            Aminah Ådin
          </a>
          <div className="hidden md:flex items-center gap-9 text-sm text-[#5C5651]">
            <a href="#igenkanning" data-testid="nav-igenkanning" className="hover:text-[#2C2825] transition-colors">Känner du igen dig?</a>
            <a href="#samtalet" data-testid="nav-samtalet" className="hover:text-[#2C2825] transition-colors">Samtalet</a>
            <a href="#om" data-testid="nav-about" className="hover:text-[#2C2825] transition-colors">Vem är jag?</a>
            <a href="#faq" data-testid="nav-faq" className="hover:text-[#2C2825] transition-colors">Vanliga frågor</a>
          </div>
          <button
            type="button"
            onClick={scrollToBooking}
            data-testid="nav-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#8C7362] hover:bg-[#6B5547] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors"
          >
            Boka samtal
          </button>
        </nav>
      </header>

      {/* Hero */}
      <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[60%] h-[80%] bg-gradient-to-bl from-[#F3EFEA] via-[#F3EFEA]/40 to-transparent rounded-bl-[40%]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-up">
            <p
              className="text-xs sm:text-sm tracking-[0.3em] uppercase text-[#8C7362] mb-6"
              data-testid="hero-eyebrow"
            >
              Boka ett kostnadsfritt 30-minuterssamtal med mig
            </p>
            <h1
              data-testid="hero-headline"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#2C2825] leading-[1.05] tracking-tight"
            >
              Från inre stress och oro till <em className="not-italic text-[#8C7362]">lugn, glädje och styrka</em>.
            </h1>
            <p
              data-testid="hero-subhead"
              className="mt-7 text-lg md:text-xl text-[#5C5651] leading-relaxed max-w-2xl"
            >
              Samtal och mentorprogram för dig som vill landa i ett inre lugn
              och stärka upp dina styrkor – så att du kan må bättre och göra
              mer av det du älskar!
            </p>

            <div className="mt-9">
              <button
                type="button"
                onClick={scrollToBooking}
                data-testid="hero-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#8C7362] hover:bg-[#6B5547] text-white px-10 py-5 text-sm font-bold tracking-[0.18em] uppercase transition-colors shadow-sm"
              >
                Boka ditt kostnadsfria samtal här
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-200">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E6DFD7] rounded-[120px_120px_28px_28px] -z-10" />
              <img
                src={HERO_IMG}
                alt="Aminah Ådin – mentor, föreläsare och författare"
                className="w-full h-[420px] sm:h-[560px] object-cover object-[center_top] rounded-[120px_120px_28px_28px] shadow-sm"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section id="igenkanning" className="py-20 md:py-28 bg-[#F3EFEA]/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-5">
              Känner du igen dig?
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#2C2825] leading-tight">
              Kan du känna igen dig i detta?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-7" data-testid="painpoints-grid">
            {painPoints.map((p, i) => (
              <div
                key={i}
                data-testid={`painpoint-${i}`}
                className="bg-white border border-[#E6DFD7] rounded-3xl p-8 md:p-10 relative"
              >
                <span className="font-serif-display text-6xl text-[#E6DFD7] absolute top-4 right-6 leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#2C2825] leading-snug relative">
                  {p.title}
                </h3>
                <p className="mt-4 text-[#5C5651] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <button
              type="button"
              onClick={scrollToBooking}
              data-testid="painpoints-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#8C7362] hover:bg-[#6B5547] text-white px-10 py-5 text-sm font-bold tracking-[0.18em] uppercase transition-colors shadow-sm"
            >
              Boka ditt kostnadsfria samtal
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Solution / Samtalet */}
      <section id="samtalet" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-5">
              Lösningen
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#2C2825] leading-[1.1]">
              Det finns ett mjukare och mer hållbart sätt.
            </h2>
            <p className="mt-6 text-[#5C5651] text-base md:text-lg leading-relaxed">
              Målet är inte att du ska göra stora förändringar. Det handlar om
              att lära känna dig själv på ett nytt sätt – där du kan vara den
              du är. Du kommer att få stöd att synliggöra och kartlägga dina
              mönster, och få enkla övningar som passar din vardag. På så sätt
              blir förändringen hållbar och du får ett välmående som stärks
              över tid.
            </p>
          </div>

          <p className="mt-16 text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-8">
            Ett samtal med mig handlar om:
          </p>
          <div
            data-testid="solution-block"
            className="bg-[#F3EFEA] border border-[#E6DFD7] rounded-3xl p-8 md:p-12 max-w-3xl mx-auto"
          >
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#2C2825] leading-snug">
              Så går samtalet till:
            </h3>
            <p className="mt-5 text-[#5C5651] text-base md:text-lg leading-relaxed">
              Vi pratar i 30 minuter om var du befinner dig just nu och vad du
              längtar efter. Du bestämmer själv vad du vill ta upp. Om mitt
              mentorprogram kan passa dig berättar jag gärna om det – annars
              får du ändå med dig en enkel övning du kan börja med direkt. Det
              är en stärkande stund för dig, utan måsten eller krav.
            </p>
          </div>

          <div className="mt-14 text-center">
            <button
              type="button"
              onClick={scrollToBooking}
              data-testid="solutions-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#8C7362] hover:bg-[#6B5547] text-white px-10 py-5 text-sm font-bold tracking-[0.18em] uppercase transition-colors shadow-sm"
            >
              Boka ditt kostnadsfria samtal
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* About – Vem är jag? */}
      <section id="om" className="py-24 md:py-32 bg-[#F3EFEA]/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-[#E6DFD7] rounded-[28px_28px_120px_120px] -z-10" />
              <img
                src={ABOUT_IMG}
                alt="Aminah Ådin"
                className="w-full h-[440px] md:h-[560px] object-cover object-center rounded-[28px_28px_120px_120px]"
                data-testid="about-image"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-5">
              Vem är jag?
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#2C2825] leading-tight">
              Aminah Ådin
            </h2>

            <figure className="mt-8 border-l-2 border-[#A98C78] pl-6 md:pl-8">
              <Quote className="h-6 w-6 text-[#A98C78] mb-4" />
              <blockquote className="font-serif-display text-2xl md:text-3xl text-[#2C2825] leading-snug italic">
                &ldquo;Jag vet hur det är. Jag har själv levt med depression,
                stress och smärta under större delen av mitt liv. För mig blev
                vändpunkten att sluta kämpa emot mina känslor och istället lära
                mig att vara snäll med mig själv. Då mådde jag bättre och livet
                blev lättare.&rdquo;
              </blockquote>
            </figure>

            <div className="mt-8 space-y-5 text-[#5C5651] text-base md:text-lg leading-relaxed">
              <p>
                Jag heter Aminah Ådin och är socionom, föreläsare, författare
                och mentor. Genom min egen resa och min yrkeserfarenhet hjälper
                jag människor att känna sig lugnare och starkare och att bygga
                en trygg relation till sig själva och sina nära.
              </p>
              <p>
                Min bok{" "}
                <em className="text-[#2C2825]">Flickan som blev kvar – Att
                finna lugn och lycka efter trauman</em> har uppmärksammats i
                bland annat TV4 Nyhetsmorgon, Expressen och i ett flertal
                poddar.
              </p>
              <p>
                Nu öppnar jag upp nya platser för dig som kvinna att få stöd
                via mitt mentorprogram och i enstaka samtal.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Tag>Föreläsare</Tag>
              <Tag>Författare</Tag>
              <Tag>Mentor</Tag>
              <Tag>Socionom</Tag>
            </div>

            <div className="mt-10">
              <button
                type="button"
                onClick={scrollToBooking}
                data-testid="about-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#8C7362] hover:bg-[#6B5547] text-white px-10 py-5 text-sm font-bold tracking-[0.18em] uppercase transition-colors shadow-sm"
              >
                Boka ditt kostnadsfria samtal
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-5">
              Omdömen
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#2C2825] leading-tight">
              Andras ord om Aminah som mentor
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-7" data-testid="testimonials-grid">
            {testimonials.map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-[#F3EFEA] border border-[#E6DFD7] rounded-3xl p-8 md:p-9 flex flex-col"
              >
                <Quote className="h-6 w-6 text-[#A98C78] mb-5" />
                <blockquote className="font-serif-display text-xl md:text-2xl text-[#2C2825] leading-snug flex-1 italic">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-[#E6DFD7] text-sm font-medium text-[#2C2825]">
                  – {t.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Booking section with form + inline Calendly */}
      <BookingSection />

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-5">Vanliga frågor</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#2C2825] leading-tight">
              Lite trygghet inför samtalet.
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-[#DCD5CE]"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="text-left font-serif-display text-xl md:text-2xl text-[#2C2825] py-6 hover:no-underline"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C5651] text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-14 border-t border-[#E6DFD7] bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="font-serif-display text-2xl text-[#2C2825]">Aminah Ådin</p>
            <p className="text-sm text-[#8C7362] mt-1">Föreläsare · Författare · Mentor · Socionom</p>
          </div>
          <div className="text-sm text-[#5C5651] flex flex-wrap gap-x-6 gap-y-2">
            <a href="https://www.aminahadin.com/" target="_blank" rel="noreferrer" className="hover:text-[#2C2825]">aminahadin.com</a>
            <a href="#boka" className="hover:text-[#2C2825]">Boka samtal</a>
            <a href="#faq" className="hover:text-[#2C2825]">Frågor</a>
            <Link to="/integritetspolicy" className="hover:text-[#2C2825]" data-testid="footer-privacy-link">Integritetspolicy</Link>
          </div>
          <p className="text-xs text-[#8C7362]">© {new Date().getFullYear()} Aminah Ådin</p>
        </div>
      </footer>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center text-xs tracking-[0.15em] uppercase bg-white border border-[#DCD5CE] text-[#5C5651] rounded-full px-4 py-2">
      {children}
    </span>
  );
}
